import type {
  Aircraft,
  AvailabilityWindow,
  Booking,
  CertState,
  Profile,
  Warning,
} from "./types";
import { aircraftById, profileById } from "./lookups";
import { overlap } from "./time";

export interface WarningContext {
  aircraft: Aircraft[];
  profiles: Profile[];
  certs: Record<string, CertState>;
  availability: Record<string, AvailabilityWindow[]>;
}

export function evaluateWarnings(
  b: Booking,
  allBookings: Booking[],
  ctx: WarningContext,
): Warning[] {
  const warnings: Warning[] = [];
  const ac = aircraftById(ctx.aircraft, b.aircraftId);
  const inst = profileById(ctx.profiles, b.instructorId);
  const stu = profileById(ctx.profiles, b.studentId);

  if (stu && ctx.certs[stu.id]?.medical === "missing") {
    warnings.push({
      kind: "DGCA medical",
      severity: "high",
      title: "Student missing DGCA medical",
      detail: `${stu.name} does not have a current DGCA Class 2 medical on file.`,
    });
  }
  if (ac && ac.maintenanceDueDays <= 7) {
    warnings.push({
      kind: "maintenance",
      severity: "med",
      title: "CAR-M inspection due soon",
      detail: `${ac.tail} 50-hr / progressive inspection due in ${ac.maintenanceDueDays} days (CAR-M Subpart G).`,
    });
  }
  if (inst && ctx.availability[inst.id]) {
    for (const block of ctx.availability[inst.id]) {
      if (overlap(b.start, b.end, block.from, block.to)) {
        warnings.push({
          kind: "availability",
          severity: "med",
          title: "Instructor unavailable",
          detail: `${inst.name} is unavailable ${block.from}–${block.to} (${block.reason}).`,
        });
      }
    }
  }
  if (ac && allBookings) {
    for (const o of allBookings) {
      if (o.id === b.id) continue;
      if (o.aircraftId !== b.aircraftId) continue;
      if (o.status === "canceled") continue;
      if (overlap(b.start, b.end, o.start, o.end)) {
        warnings.push({
          kind: "conflict",
          severity: "block",
          title: "Aircraft already booked",
          detail: `${ac.tail} is booked ${o.start}–${o.end} for another lesson.`,
        });
      }
    }
  }
  return warnings;
}
